// AI generator catalog (Phase 3) — resolve an AI-emitted library reference into a real CUSTOM block.
//
// The generator emits ONLY {libraryBlockId, libraryVersion, parameters} — there is deliberately no `code`
// field in LibraryReference, so the AI structurally cannot supply or modify implementation code. This
// resolver always uses the immutable version's `entry.code`, validates the AI's parameter values against
// the declared schema (rejecting out-of-bounds / wrong-type / unknown params), and stamps provenance.
// Same validation is reused by the optimizer pool (Phase 4) so both consumers share one safety contract.

import { buildEntryBlock } from '../../services/customBlockLibraryService';
import { validateParameters } from './validateParams';
import type { Block } from '../../types';
import type { LibraryEntry } from '../../types/customBlockLibrary';

export interface LibraryReference {
  libraryBlockId: string;
  libraryVersion: number;
  parameters?: Record<string, unknown>;
  // intentionally NO `code` — see module header.
}

export { validateParameters } from './validateParams';

/** Resolve an AI reference → a provenance-stamped CUSTOM block, or an error. The block's code is ALWAYS
 *  the immutable version's code (`entry.code`); any code on the reference is ignored by construction. */
export function resolveLibraryReference(ref: LibraryReference, entry: LibraryEntry | undefined): { block: Block } | { error: string } {
  if (!entry) return { error: `library block ${ref.libraryBlockId} not found / not ai_safe / disabled` };
  if (ref.libraryVersion !== entry.version) return { error: `version mismatch: requested v${ref.libraryVersion}, available v${entry.version} (versions are immutable)` };
  const v = validateParameters(entry.paramSchema, ref.parameters ?? {});
  if (!v.ok) return { error: `invalid parameters: ${v.errors.join('; ')}` };
  let block: Block;
  try {
    block = buildEntryBlock(entry); // TSL AST + provenance from the immutable entry (throws on codeful legacy entries)
  } catch (e) {
    return { error: String((e as Error)?.message ?? e) };
  }
  (block.data as { parameters?: Record<string, unknown> }).parameters = v.values; // AI configures declared params only
  return { block };
}
